import { CLAIM_OUTCOME_LABELS } from '@/components/ClaimsTable'
import { Timeline, TimelineStep, type TimelineStepState } from '@/components/ui/timeline'
import { formatDate, formatDateTime } from '@/lib/utils'
import { type Claim, ClaimOutcome, ClaimStatus } from '@/models/claim'

const ORDER: ClaimStatus[] = [
  ClaimStatus.DRAFT,
  ClaimStatus.SUBMITTED,
  ClaimStatus.FILED,
  ClaimStatus.OPEN_WITH_CARRIER,
  ClaimStatus.CLOSED,
]

// Neither is on the path any more; both sit with Cai, which is what Submitted means.
const OFF_PATH: Partial<Record<ClaimStatus, ClaimStatus>> = {
  [ClaimStatus.IN_REVIEW]: ClaimStatus.SUBMITTED,
  [ClaimStatus.INFO_NEEDED]: ClaimStatus.SUBMITTED,
}

const OUTCOME_COPY: Record<ClaimOutcome, string> = {
  [ClaimOutcome.PAID]: 'The carrier paid this claim.',
  [ClaimOutcome.DENIED]: 'The carrier denied this claim.',
  [ClaimOutcome.CLOSED_NO_PAYMENT]: 'The carrier closed this claim without paying on it.',
  [ClaimOutcome.WITHDRAWN]: 'This claim was withdrawn.',
}

const OUTCOME_TONES: Record<ClaimOutcome, 'success' | 'danger' | 'neutral'> = {
  [ClaimOutcome.PAID]: 'success',
  [ClaimOutcome.DENIED]: 'danger',
  [ClaimOutcome.CLOSED_NO_PAYMENT]: 'neutral',
  [ClaimOutcome.WITHDRAWN]: 'neutral',
}

const CARRIER_OUTCOMES: ClaimOutcome[] = [
  ClaimOutcome.PAID,
  ClaimOutcome.DENIED,
  ClaimOutcome.CLOSED_NO_PAYMENT,
]

/**
 * A stage is done once it, or any stage after it, has been reached — a claim sitting
 * with an adjuster was filed, whether or not anyone recorded the filing. A closure
 * counts only when the carrier ended the claim (paid, denied, no payment), as it can
 * only end one that was filed and worked; a withdrawal, or a close with no outcome,
 * reaches Closed from anywhere and proves nothing.
 */
function reached(step: ClaimStatus, claim: Claim): boolean {
  if (step === ClaimStatus.CLOSED) return claim.closedAt != null
  const endedByCarrier = claim.closedAt != null && !!claim.outcome && CARRIER_OUTCOMES.includes(claim.outcome)
  if (endedByCarrier) return true

  const STAMPS: Partial<Record<ClaimStatus, string | null>> = {
    [ClaimStatus.SUBMITTED]: claim.submittedAt,
    [ClaimStatus.FILED]: claim.filedAt,
    [ClaimStatus.OPEN_WITH_CARRIER]: claim.carrierOpenedAt,
  }
  const implying = ORDER.slice(ORDER.indexOf(step), ORDER.indexOf(ClaimStatus.CLOSED))
  return implying.some((stage) => STAMPS[stage] != null)
}

function stateFor(step: ClaimStatus, claim: Claim): TimelineStepState {
  const at = OFF_PATH[claim.status] ?? claim.status
  if (at === step) return at === ClaimStatus.CLOSED ? 'done' : 'current'
  return reached(step, claim) ? 'done' : 'pending'
}

function when(value?: string | null) {
  return value ? formatDateTime(value) : undefined
}

/** Stacked lines, so a staff note reads under the facts rather than replacing them. */
function Lines({ of }: { of: (string | undefined | null | false)[] }) {
  const lines = of.filter(Boolean) as string[]
  if (lines.length === 0) return null
  return (
    <span className="flex flex-col gap-1">
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </span>
  )
}

export function ClaimLifecycle({ claim }: { claim: Claim }) {
  const at = OFF_PATH[claim.status] ?? claim.status
  const submitted = reached(ClaimStatus.SUBMITTED, claim)
  const closed = at === ClaimStatus.CLOSED
  const outcome = claim.outcome ?? undefined
  const filed = stateFor(ClaimStatus.FILED, claim)
  const withCarrier = stateFor(ClaimStatus.OPEN_WITH_CARRIER, claim)
  /** A note for a stage nobody has reached would describe work that has not happened. */
  const note = (step: ClaimStatus) =>
    stateFor(step, claim) === 'pending' ? undefined : claim.statusNotes[step] || undefined
  /** What a step promises next, which is moot once the claim has moved past it. */
  const upcoming = (step: ClaimStatus, copy: string) =>
    ORDER.indexOf(at) > ORDER.indexOf(step) ? undefined : copy

  return (
    <Timeline>
      <TimelineStep
        title="Submitted"
        meta={when(claim.submittedAt)}
        metaPosition="below"
        state={submitted ? 'done' : 'pending'}
        description={
          submitted ? (
            <Lines
              of={[
                claim.submittedByName && `Submitted by ${claim.submittedByName}.`,
                at === ClaimStatus.SUBMITTED && 'The Cai team is reviewing your submission.',
                note(ClaimStatus.SUBMITTED),
              ]}
            />
          ) : (
            upcoming(ClaimStatus.SUBMITTED, 'Lands here once you submit the claim.')
          )
        }
      />
      <TimelineStep
        title="Filed to carrier"
        meta={when(claim.filedAt)}
        metaPosition="below"
        state={filed}
        description={
          filed === 'pending' ? (
            upcoming(ClaimStatus.FILED, 'We file this as soon as your broker signs off.')
          ) : (
            <Lines
              of={[
                'Filed as a first notice of loss.',
                claim.carrierReference &&
                  `The carrier's reference for this claim is ${claim.carrierReference}.`,
                at === ClaimStatus.FILED && 'An adjuster will be assigned to this claim soon.',
                note(ClaimStatus.FILED),
              ]}
            />
          )
        }
      />
      <TimelineStep
        title="In process with the carrier"
        metaPosition="below"
        state={withCarrier}
        description={
          withCarrier === 'pending' ? (
            upcoming(
              ClaimStatus.OPEN_WITH_CARRIER,
              'The carrier assigns an adjuster, who works the claim from here.'
            )
          ) : claim.adjusterName || claim.adjusterContact ? (
            <Lines
              of={[
                claim.adjusterName
                  ? `Adjuster ${claim.adjusterName}${
                      claim.adjusterContact ? ` · ${claim.adjusterContact}` : ''
                    }.`
                  : `Adjuster contact: ${claim.adjusterContact}.`,
                claim.adjusterAssignedAt && `Assigned on ${formatDate(claim.adjusterAssignedAt)}.`,
                note(ClaimStatus.OPEN_WITH_CARRIER),
              ]}
            />
          ) : (
            <Lines
              of={[
                'The carrier assigns an adjuster, who works the claim from here.',
                note(ClaimStatus.OPEN_WITH_CARRIER),
              ]}
            />
          )
        }
      />
      <TimelineStep
        title={closed && outcome ? `Closed · ${CLAIM_OUTCOME_LABELS[outcome]}` : 'Closed'}
        meta={when(claim.closedAt)}
        metaPosition="below"
        state={stateFor(ClaimStatus.CLOSED, claim)}
        tone={closed && outcome ? OUTCOME_TONES[outcome] : undefined}
        description={
          closed ? (
            <Lines of={[outcome && OUTCOME_COPY[outcome], note(ClaimStatus.CLOSED)]} />
          ) : (
            'The carrier closes the claim once it settles, is denied, or is withdrawn.'
          )
        }
      />
    </Timeline>
  )
}
